import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ title, description, keywords, ogImage }) => {
  const location = useLocation();
  const baseUrl = window.location.origin;
  const currentUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', 'website', true);
    if (ogImage) {
      updateMetaTag('og:image', `${baseUrl}${ogImage}`, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (ogImage) {
      updateMetaTag('twitter:image', `${baseUrl}${ogImage}`);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

  }, [title, description, keywords, ogImage, currentUrl, baseUrl]);

  return null;
};

export default SEO;

// SEO data for each page
export const seoData = {
  home: {
    title: "The Br8k Room | Premium VR & Entertainment | Michigan City, IN",
    description: "Michigan City's premier entertainment destination! Premium Zero Latency VR, competitive gaming, 3 retro consoles with 40,000+ games each, and The Break Zone. 7,500 sq ft of epic fun for all ages 12+.",
    keywords: "VR arcade Michigan City, virtual reality entertainment, Zero Latency VR, retro gaming, Break Zone Michigan City, birthday parties, team building activities, family entertainment",
    ogImage: "/logo.jpeg"
  },
  attractions: {
    title: "VR Attractions | Zero Latency, Battle Start & More | The Br8k Room",
    description: "Experience 8 unique attractions: Premium Zero Latency VR (1,053 sq ft arena), Battle Start, Vex Play, Spawnpoint, VRCave, retro gaming, The Break Zone, and more!",
    keywords: "Zero Latency VR, Battle Start VR, VRCave, free-roam VR, retro arcade games, Break Zone, VR experiences Michigan City",
    ogImage: "/zero-latency.png"
  },
  pricing: {
    title: "Pricing & Packages | Simple, Transparent Rates | The Br8k Room",
    description: "Simple pricing starting at $10/hour for retro gaming, $25 for VR sessions. Premium Zero Latency from $30. Group discounts available. Ages 12+ for VR, 13+ for Break Zone.",
    keywords: "VR pricing Michigan City, affordable VR arcade, Zero Latency cost, entertainment pricing, group discounts, birthday party packages",
    ogImage: "/logo.jpeg"
  },
  events: {
    title: "Birthday Parties & Group Events | The Br8k Room | Michigan City",
    description: "Host unforgettable birthday parties and group events! Packages from $299 (8 guests) to $899 (16 guests). Perfect for birthdays, team building, and celebrations.",
    keywords: "birthday parties Michigan City, VR birthday party, team building activities, group events, corporate parties, kids birthday parties",
    ogImage: "/logo.jpeg"
  },
  about: {
    title: "About Us | Locally Owned Entertainment Center | The Br8k Room",
    description: "Family-owned 7,500 sq ft entertainment center in Michigan City. We bring people together through premium VR, retro gaming, and unforgettable experiences. Community-focused since day one.",
    keywords: "Michigan City entertainment, local business, family entertainment center, VR arcade near me, community gathering place",
    ogImage: "/logo.jpeg"
  },
  contact: {
    title: "Contact Us | Visit, Call, or Book Online | The Br8k Room",
    description: "The Br8k Room - 2060 E US Highway 20, Michigan City, IN 46360. Call (219) 243-7905. Open Mon-Thu 4pm-9pm, Fri-Sat 1pm-9pm. Closed Sundays.",
    keywords: "contact The Br8k Room, VR arcade location, Michigan City entertainment center, hours, phone number, directions",
    ogImage: "/logo.jpeg"
  },
  faq: {
    title: "FAQ | Common Questions Answered | The Br8k Room",
    description: "Get answers to common questions about age requirements (12+ for VR, 13+ for Break Zone), safety, booking, what to wear, and more at The Br8k Room.",
    keywords: "VR FAQ, age requirements, safety guidelines, booking information, what to wear VR, Michigan City VR questions",
    ogImage: "/logo.jpeg"
  },
  gallery: {
    title: "Photo Gallery | See The Br8k Room in Action | Michigan City",
    description: "Browse photos of our VR attractions, facility, and happy customers. See Zero Latency, Battle Start, retro gaming, and The Break Zone in action!",
    keywords: "VR arcade photos, The Br8k Room gallery, Zero Latency images, entertainment center photos Michigan City",
    ogImage: "/zero-latency.png"
  },
  booking: {
    title: "Book Your Experience | Reserve Your Spot | The Br8k Room",
    description: "Book your VR experience online! Choose your date, time, and attraction. Reservations recommended. Walk-ins welcome based on availability.",
    keywords: "book VR Michigan City, online reservation, schedule VR session, book Zero Latency, reserve entertainment",
    ogImage: "/logo.jpeg"
  }
};
